import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { dashboardKeys } from "@/lib/api/dashboard";
import type {
  TeamMembersResponse,
  TeamInvitesResponse,
  CreateInviteResponse,
  InvitePreviewResponse,
  AcceptInviteResponse,
  InviteRole,
  AuditLogResponse,
} from "@/lib/api/types";

export const teamsKeys = {
  all: ["teams"] as const,
  members: (tenantId: string) =>
    [...teamsKeys.all, "members", tenantId] as const,
  invites: (tenantId: string) =>
    [...teamsKeys.all, "invites", tenantId] as const,
  invitePreview: (token: string) =>
    [...teamsKeys.all, "invite-preview", token] as const,
  auditLog: (tenantId: string) =>
    [...teamsKeys.all, "audit-log", tenantId] as const,
};

export const teamMembersQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: teamsKeys.members(tenantId),
    queryFn: () =>
      apiClient
        .get<TeamMembersResponse>(`/api/tenants/${tenantId}/members`)
        .then((r) => r.data),
    enabled: !!tenantId,
  });

export function useTeamMembers(tenantId: string) {
  return useQuery(teamMembersQueryOptions(tenantId));
}

export function useUpdateMemberRole(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: InviteRole }) =>
      apiClient
        .patch(`/api/tenants/${tenantId}/members/${userId}`, { role })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamsKeys.members(tenantId) });
    },
  });
}

export function useRemoveMember(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      apiClient
        .delete(`/api/tenants/${tenantId}/members/${userId}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamsKeys.members(tenantId) });
    },
  });
}

export const teamInvitesQueryOptions = (tenantId: string) =>
  queryOptions({
    queryKey: teamsKeys.invites(tenantId),
    queryFn: () =>
      apiClient
        .get<TeamInvitesResponse>(`/api/tenants/${tenantId}/invites`)
        .then((r) => r.data),
    enabled: !!tenantId,
  });

export function useTeamInvites(tenantId: string) {
  return useQuery(teamInvitesQueryOptions(tenantId));
}

export function useCreateInvite(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      role,
      expiresInDays,
    }: {
      role: InviteRole;
      expiresInDays?: number;
    }) =>
      apiClient
        .post<CreateInviteResponse>(`/api/tenants/${tenantId}/invites`, {
          role,
          expiresInDays,
        })
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamsKeys.invites(tenantId) });
    },
  });
}

export function useDeleteInvite(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) =>
      apiClient
        .delete(`/api/tenants/${tenantId}/invites/${inviteId}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamsKeys.invites(tenantId) });
    },
  });
}

const invitePreviewQueryOptions = (token: string) =>
  queryOptions({
    queryKey: teamsKeys.invitePreview(token),
    queryFn: () =>
      apiClient
        .get<InvitePreviewResponse>(`/api/tenants/invites/${token}/preview`)
        .then((r) => r.data),
    enabled: !!token,
    retry: false,
  });

export function useInvitePreview(token: string) {
  return useQuery(invitePreviewQueryOptions(token));
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) =>
      apiClient
        .post<AcceptInviteResponse>(`/api/tenants/invites/${token}/accept`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: teamsKeys.all });
    },
  });
}

export function useLeaveTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) =>
      apiClient.post(`/api/tenants/${tenantId}/leave`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: teamsKeys.all });
    },
  });
}

export function useTeamAuditLog(tenantId: string) {
  return useQuery({
    queryKey: teamsKeys.auditLog(tenantId),
    queryFn: () =>
      apiClient
        .get<AuditLogResponse>(`/api/tenants/${tenantId}/audit-log`)
        .then((r) => r.data),
    enabled: !!tenantId,
  });
}
